import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { invitationAPI } from '../../services/api';
import { useToast } from '../Toast';
import { formatDateToIST, isDateInPast } from '../../utils/dateUtils';
import ConfirmationModal from '../ConfirmationModal';

const Invitations = () => {
  const [filters, setFilters] = useState({
    includeExpired: false,
    skip: 0,
    limit: 20
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { showToast } = useToast();

  // Fetch invitations
  const { data: invitationsResponse, isLoading, error } = useQuery({
    queryKey: ['invitations', filters],
    queryFn: () => invitationAPI.getAll(filters.skip, filters.limit, filters.includeExpired),
    keepPreviousData: true
  });

  // Fetch stats
  const { data: statsResponse, isLoading: statsLoading } = useQuery({
    queryKey: ['invitation-stats'],
    queryFn: () => invitationAPI.getStats()
  });

  // Extract data from responses
  const invitationsData = invitationsResponse?.data;
  const stats = statsResponse?.data;

  // Debug logging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Invitations component - Data:', invitationsData?.invitations?.length || 0, 'invitations');
    console.log('🔍 Invitations component - Stats:', stats);
  }

  // Resend invitation mutation
  const resendMutation = useMutation({
    mutationFn: (id) => invitationAPI.resend(id),
    onSuccess: () => {
      showToast('Invitation email resent successfully', 'success');
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to resend invitation', 'error');
    }
  });

  // Delete/Revoke invitation mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => invitationAPI.delete(id),
    onSuccess: () => {
      const message = deleteTarget?.isAccepted
        ? 'User access revoked successfully'
        : 'Invitation revoked successfully';
      showToast(message, 'success');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      // Refetch invitations to update the list
      window.location.reload();
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to revoke access', 'error');
    }
  });

  const handleResend = (id) => {
    resendMutation.mutate(id);
  };

  const handleDelete = (id, email, isAccepted) => {
    setDeleteTarget({ id, email, isAccepted });
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
    }
  };

  const getStatusIcon = (invitation) => {
    if (invitation.is_accepted) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    if (isDateInPast(invitation.expires_at)) {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
    return <ClockIcon className="h-5 w-5 text-yellow-500" />;
  };

  const getStatusText = (invitation) => {
    if (invitation.is_accepted) {
      return 'Accepted';
    }
    if (isDateInPast(invitation.expires_at)) {
      return 'Expired';
    }
    return 'Pending';
  };

  const getStatusColor = (invitation) => {
    if (invitation.is_accepted) {
      return 'bg-green-900/30 text-green-300 border border-green-700';
    }
    if (isDateInPast(invitation.expires_at)) {
      return 'bg-red-900/30 text-red-300 border border-red-700';
    }
    return 'bg-yellow-900/30 text-yellow-300 border border-yellow-700';
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading invitations
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error.response?.data?.detail || error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Invitations</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage user invitations and track their status
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/invitations/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Invitation
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-900 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-900 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <EnvelopeIcon className="h-6 w-6 text-gray-400 dark:text-gray-300" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Total Invitations
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {stats.total_invitations || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Pending
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {stats.pending_invitations || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Accepted
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {stats.accepted_invitations || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Expired
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {stats.expired_invitations || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.includeExpired}
                onChange={(e) => setFilters(prev => ({ ...prev, includeExpired: e.target.checked, skip: 0 }))}
                className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 dark:bg-gray-800 dark:border-gray-600 dark:focus:border-blue-400"
                style={{ minHeight: '10px', minWidth: '10px', maxHeight: '10px', maxWidth: '10px' }}
              />
              <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">Include expired invitations</span>
            </label>
          </div>
        </div>
      </div>

      {/* Invitations List */}
      <div className="bg-white dark:bg-gray-900 shadow overflow-hidden sm:rounded-md">
        {isLoading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading invitations...</p>
          </div>
        ) : invitationsData?.invitations && invitationsData.invitations.length > 0 ? (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {invitationsData.invitations.map((invitation) => (
              <li key={invitation.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {getStatusIcon(invitation)}
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {invitation.email}
                        </p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invitation)}`}>
                          {getStatusText(invitation)}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <span>Role: {invitation.role.replace('_', ' ').toLowerCase()}</span>
                        <span className="mx-2">•</span>
                        <span>Invited by: {invitation.invited_by}</span>
                        {!invitation.is_accepted && (
                          <>
                            <span className="mx-2">•</span>
                            <span>Expires: {formatDateToIST(invitation.expires_at)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!invitation.is_accepted && !isDateInPast(invitation.expires_at) && (
                      <button
                        onClick={() => handleResend(invitation.id)}
                        disabled={resendMutation.isLoading}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900 disabled:opacity-50"
                      >
                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                        Resend
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(invitation.id, invitation.email, invitation.is_accepted)}
                      disabled={deleteMutation.isLoading}
                      className="inline-flex items-center px-3 py-1 border border-red-300 dark:border-red-600 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-900 disabled:opacity-50"
                      title={invitation.is_accepted ? "Revoke Access (Deactivate User)" : "Revoke Invitation"}
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Revoke
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-8 text-center">
            <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No invitations</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {error ? 'Failed to load invitations' : 'Get started by creating a new invitation.'}
            </p>
            {!error && (
              <div className="mt-6">
                <Link
                  to="/invitations/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  New Invitation
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {invitationsData && invitationsData.total > filters.limit && (
          <div className="bg-white dark:bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setFilters(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                disabled={filters.skip === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                disabled={filters.skip + filters.limit >= invitationsData.total}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{' '}
                  <span className="font-medium">{filters.skip + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(filters.skip + filters.limit, invitationsData.total)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{invitationsData.total}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                    disabled={filters.skip === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                    disabled={filters.skip + filters.limit >= invitationsData.total}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete/Revoke Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        title={deleteTarget?.isAccepted ? "Revoke User Access" : "Revoke Invitation"}
        message={
          deleteTarget?.isAccepted
            ? `Are you sure you want to revoke access for ${deleteTarget?.email}? This will immediately deactivate their user account and prevent them from logging in.`
            : `Are you sure you want to revoke the invitation for ${deleteTarget?.email}? This will prevent them from accepting the invitation and joining your organization.`
        }
        confirmText={deleteTarget?.isAccepted ? "Revoke Access" : "Revoke Invitation"}
        cancelText="Cancel"
        type="danger"
        confirmButtonVariant="danger"
      />
    </div>
  );
};

export default Invitations; 